import React, { useEffect, useState } from 'react';
import { SCUIGeneralDrawerHeader } from '../../../../../atomic/molecules';
import { Grid, TextField, FormHelperText, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import InputMask from 'react-input-mask';
import { useTranslation } from 'react-i18next';
import { SCUIAutoCompleteInput, SCUIButton } from '../../../../../atomic/atoms';
import { ReturnQuery } from '../../../../../services/query';
import Helper from '../../../../../utils/helper';
import { RCEDC } from './returnCardEditDrawerContent.classes';
import {
    FieldConfig,
    getCountryIdFromIban,
    validateReturnCardField,
    ValidationRuleType,
} from '../returnCardFieldValidation.helper';
import {
    extractCountryListFromPageRulesPayload,
    getReturnPageRulesCountryListCache,
    setReturnPageRulesCountryListCache,
} from '../returnPageRules.helper';

export { ValidationRuleType };

interface ReturnCardEditDrawerContentProps {
    bankList: any[];
    selectedBankAccount: any;
    onClose: () => void;
    onUpdateBankAccount: (success: boolean, formData: any) => void;
}

export const ReturnCardEditDrawerContent: React.FC<ReturnCardEditDrawerContentProps> = ({
    bankList,
    selectedBankAccount,
    onClose,
    onUpdateBankAccount,
}) => {
    const { t } = useTranslation();
    const [bankConfig, setBankConfig] = useState<FieldConfig[]>([]);
    const [countries, setCountries] = useState<any[]>([]);
    const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
    const [formData, setFormData] = useState<any>({});
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
    const CountryId = Helper.GetCurrentCountry();
    const BOSNIA_COUNTRY_CODE = 138;
    const isFormValid = (): boolean => {
        if (bankConfig.length === 0) return false;

        const allRequiredFieldsFilled = bankConfig.every((field) => {
            const value = formData[field.fieldName];
            if (!field.isRequired) return true;

            if (field.isSelection) {
                return value !== undefined && value !== null && value !== '';
            }

            return value && value.toString().trim() !== '';
        });

        const allValid = bankConfig.every((field) => {
            const value = formData[field.fieldName] || '';
            return validateField(field, value.toString()) === '';
        });

        return allRequiredFieldsFilled && allValid;
    };
    const getFieldsForCountry = (countriesData: any[], countryId: number): FieldConfig[] => {
        if (!countriesData?.length) return [];
        const matchedCountry = countriesData.find((c) => c.countryId === countryId);
        return matchedCountry?.fields || countriesData[0]?.fields || [];
    };

    const fetchPageRules = async (cId?: number) => {
        try {
            const requestCountryId = cId !== undefined ? cId : CountryId;
            const response = await ReturnQuery.GetPageRulesList({ CountryId: requestCountryId });
            const payload = response?.data || response;

            const countryList = extractCountryListFromPageRulesPayload(payload);

            setCountries(countryList);
            setReturnPageRulesCountryListCache(countryList);

            const countriesData = payload?.countries || [];
            let activeCountryId = cId !== undefined ? cId : requestCountryId;

            if (cId === undefined) {
                const ibanCountryId = getCountryIdFromIban(selectedBankAccount?.ibanNumber, countryList);

                if (ibanCountryId !== null) {
                    activeCountryId = ibanCountryId;
                } else if (countryList.length > 0) {
                    const contextCountryInList = countryList.find((c: any) => c.countryId === requestCountryId);
                    activeCountryId = contextCountryInList ? requestCountryId : countryList[0].countryId;
                } else if (countriesData.length > 0) {
                    activeCountryId = countriesData[0].countryId;
                }

                setSelectedCountryId(activeCountryId);
            } else {
                setSelectedCountryId(cId);
            }

            let fields = getFieldsForCountry(countriesData, activeCountryId);

            if (fields.length === 0 && payload?.fields) {
                fields = payload.fields;
            }

            if (cId === undefined && fields.length === 0 && activeCountryId !== requestCountryId) {
                await fetchPageRules(activeCountryId);
                return;
            }

            if (cId === undefined && fields.length === 0 && countryList.length > 1) {
                await fetchPageRules(activeCountryId);
                return;
            }

            const sortedFields =
                fields.length > 0
                    ? [...fields].sort(
                          (a: FieldConfig, b: FieldConfig) => (a.displayOrder || 0) - (b.displayOrder || 0)
                      )
                    : [];

            setBankConfig(sortedFields);
        } catch (error) {
            console.error('Error fetching page rules:', error);
            setBankConfig([]);
        }
    };

    useEffect(() => {
        const cachedCountryList = getReturnPageRulesCountryListCache();
        const ibanCountryId =
            selectedBankAccount?.ibanNumber && cachedCountryList
                ? getCountryIdFromIban(selectedBankAccount.ibanNumber, cachedCountryList)
                : null;

        if (ibanCountryId !== null) {
            fetchPageRules(ibanCountryId);
            return;
        }

        fetchPageRules();
    }, []);

    const handleCountryChange = (event: any) => {
        const newCountryId = event.target.value;
        setSelectedCountryId(newCountryId);
        fetchPageRules(newCountryId);
    };

    useEffect(() => {
        if (selectedBankAccount) {
            const accountOwner = selectedBankAccount.accountOwner || '';
            const initialValues = {
                nameSurname: accountOwner,
                accountHolder: accountOwner,
                accountNumber: selectedBankAccount.accountNumber || '',
                swiftCode: selectedBankAccount.bankCode || '',
                iban: selectedBankAccount.ibanNumber || '',
                bankAccountId: selectedBankAccount.bankAccountId,
                bankSelection: selectedBankAccount.bankId,
                fatherName: selectedBankAccount.accountFatherName || '',
                nationalId: selectedBankAccount.nationalId || '',
                mfoCode: selectedBankAccount?.mfo || '-',
                personalNumber: selectedBankAccount.personalNumber || '',
                street: selectedBankAccount.street || '',
                house: selectedBankAccount.house || '',
                postalCode: selectedBankAccount.postalCode || '',
            };
            setFormData(initialValues);

            // Initial values set edildikten sonra validasyon hatalarını temizle
            setValidationErrors({});
        }
    }, [selectedBankAccount, bankList]);

    const validateField = (field: FieldConfig, value: string): string =>
        validateReturnCardField(field, value, t);

    const validateAllFields = (): boolean => {
        const errors: { [key: string]: string } = {};
        let isValid = true;

        bankConfig.forEach((field) => {
            const value = formData[field.fieldName] || '';
            const error = validateField(field, value);
            if (error) {
                errors[field.fieldName] = error;
                isValid = false;
            }
        });

        setValidationErrors(errors);
        return isValid;
    };

    const resolveFieldValue = (field: FieldConfig, value: unknown): string => {
        if (field.isSelection) {
            const option = value as { value?: string | number; showedText?: string } | null | undefined;
            if (CountryId === BOSNIA_COUNTRY_CODE && field.fieldName === 'postalCode') {
                return option?.showedText ?? '';
            }
            return option?.value !== undefined && option?.value !== null ? String(option.value) : '';
        }

        if (typeof value === 'string') {
            return value;
        }

        const input = value as { value?: string } | null | undefined;
        return input?.value ?? '';
    };

    const handleFieldChange = (field: FieldConfig, value: unknown) => {
        const processedValue = resolveFieldValue(field, value);

        const updatedForm = {
            ...formData,
            [field.fieldName]: processedValue,
        };

        if (field.fieldName === 'nameSurname' || field.fieldName === 'accountHolder') {
            updatedForm.nameSurname = processedValue;
            updatedForm.accountHolder = processedValue;
        }

        setFormData(updatedForm);

        const error = validateField(field, processedValue);
        setValidationErrors((prev) => ({
            ...prev,
            [field.fieldName]: error,
        }));
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (validateAllFields()) {
            const { fatherName, ...rest } = formData;
            
            const cleanedRest = { ...rest };
            bankConfig.forEach((field) => {
                if (field.fieldPattern && typeof cleanedRest[field.fieldName] === 'string') {
                    cleanedRest[field.fieldName] = cleanedRest[field.fieldName].replace(/\s/g, '');
                }
            });

            // IBAN her durumda boşluksuz gitmeli
            if (typeof cleanedRest.iban === 'string') {
                cleanedRest.iban = cleanedRest.iban.replace(/\s/g, '');
            }

            const modifiedFormData = {
                ...cleanedRest,
                accountFatherName: fatherName,
            };
            onUpdateBankAccount(true, modifiedFormData);
        }
    };

    const renderField = (field: FieldConfig) => {
        const value = formData[field.fieldName] || '';
        const error = validationErrors[field.fieldName];

        if (field.isSelection) {
            const selectedItem =
                field.isSelection && value
                    ? (field.fieldValues || []).find((item) => {
                          if (CountryId === BOSNIA_COUNTRY_CODE && field.fieldName === 'postalCode') {
                              return item.showedText === value;
                          } else {
                              return item.value === value;
                          }
                      })
                    : null;
            return (
                <div
                    key={field.fieldName}
                    className={RCEDC.fieldWrap}
                    data-field={field.fieldName}
                    data-field-type="selection"
                >
                    <div className={RCEDC.fieldAutocompleteWrap} data-field={field.fieldName}>
                        <SCUIAutoCompleteInput
                            key={`${field.fieldName}-${selectedCountryId ?? 'default'}`}
                            list={field.fieldValues || []}
                            onChange={(event: any, newValue: any) => handleFieldChange(field, newValue || '')}
                            multiple={false}
                            txtPlaceHolder={t(`return.${field.fieldName}`)}
                            searchValue="showedText"
                            defValue={selectedItem}
                        />
                    </div>
                    {error && (
                        <FormHelperText className={RCEDC.fieldHelperText} error sx={{ ml: 2, mt: 0.5 }}>
                            {error}
                        </FormHelperText>
                    )}
                </div>
            );
        }

        if (field.fieldPattern) {
            return (
                <InputMask
                    key={field.fieldName}
                    mask={field.fieldPattern.replace(/#/g, '*')}
                    value={value}
                    onChange={(e) => handleFieldChange(field, e.target.value)}
                    maskChar={null}
                >
                    {(inputProps: any) => (
                        <TextField
                            {...inputProps}
                            className={RCEDC.fieldText}
                            data-field={field.fieldName}
                            data-field-type="text"
                            label={t(`return.${field.fieldName}`)}
                            fullWidth
                            error={!!error}
                            helperText={error}
                            slotProps={{
                                htmlInput:
                                    field.fieldName === 'accountNumber'
                                        ? {
                                              inputMode: 'numeric',
                                              pattern: '[0-9]*',
                                          }
                                        : field.fieldName === 'iban'
                                          ? {
                                                style: { textTransform: 'uppercase' },
                                            }
                                          : undefined,
                            }}
                            sx={
                                error
                                    ? {
                                          '& .MuiOutlinedInput-root': {
                                              backgroundColor: '#ffebee',
                                              '&:hover': {
                                                  backgroundColor: '#ffcdd2',
                                              },
                                          },
                                          '& .MuiFormHelperText-root': {
                                              fontSize: '11px',
                                              fontWeight: 'bold',
                                          },
                                      }
                                    : {}
                            }
                        />
                    )}
                </InputMask>
            );
        }

        return (
            <TextField
                key={field.fieldName}
                className={RCEDC.fieldText}
                data-field={field.fieldName}
                data-field-type="text"
                label={t(`return.${field.fieldName}`)}
                fullWidth
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                error={!!error}
                helperText={error}
                slotProps={{
                    htmlInput:
                        field.fieldName === 'accountNumber'
                            ? {
                                  inputMode: 'numeric',
                                  pattern: '[0-9]*',
                              }
                            : field.fieldName === 'iban'
                              ? {
                                    style: { textTransform: 'uppercase' },
                                }
                              : undefined,
                }}
                sx={
                    error
                        ? {
                              '& .MuiOutlinedInput-root': {
                                  backgroundColor: '#ffebee',
                                  '&:hover': {
                                      backgroundColor: '#ffcdd2',
                                  },
                              },
                              '& .MuiFormHelperText-root': {
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                              },
                          }
                        : {}
                }
            />
        );
    };

    return (
        <Grid className={RCEDC.root}>
            <Grid container sx={{ paddingTop: '2rem' }}>
                <SCUIGeneralDrawerHeader
                    header={t('general.editBankInfo')}
                    onClose={onClose}
                    rootClassName={RCEDC.headerRoot}
                    titleClassName={RCEDC.headerTitle}
                    closeIconClassName={RCEDC.headerCloseIcon}
                />
                <form className={RCEDC.form} onSubmit={handleSubmit}>
                    <div className={[RCEDC.container, 'commentFilterContainer'].join(' ')}>
                        <Grid className={RCEDC.fieldsGrid} container spacing={2} sx={{ lineHeight: '6rem' }}>
                            {countries.length > 1 && (
                                <Grid
                                    className={RCEDC.fieldGridItem}
                                    size={{ xl: 12, lg: 12, md: 12, xs: 12 }}
                                    sx={{ mb: 2 }}
                                >
                                    <FormControl fullWidth>
                                        <InputLabel>{t('general.country')}</InputLabel>
                                        <Select
                                            value={selectedCountryId || ''}
                                            label={t('general.country')}
                                            onChange={handleCountryChange}
                                        >
                                            {countries.map((c: any) => (
                                                <MenuItem key={c.countryId} value={c.countryId}>
                                                    {c.countryName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            {bankConfig.map((field) => (
                                <Grid
                                    key={field.fieldName}
                                    className={RCEDC.fieldGridItem}
                                    size={{
                                        xl: 12,
                                        lg: 12,
                                        md: 12,
                                        xs: 12,
                                    }}
                                >
                                    {renderField(field)}
                                </Grid>
                            ))}
                            <Grid
                                className={RCEDC.submitRow}
                                size={{ xl: 12, lg: 12, md: 12, xs: 12 }}
                                sx={{
                                    width: '100%',
                                    mt: 2,
                                    mb: 2,
                                }}
                            >
                                <SCUIButton
                                    className={RCEDC.submitButton}
                                    text={t('general.update')}
                                    type="primary"
                                    sizeType="fixed"
                                    buttonType="submit"
                                    size={'m'}
                                    fullwidth
                                    disabled={!isFormValid()}
                                />
                            </Grid>
                        </Grid>
                    </div>
                </form>
            </Grid>
        </Grid>
    );
};
